import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    SeriesSelectorViewModel,
    AggregationMode,
    KeyValuePair,
    ISortConfiguration,
    ObjectType,
    DropObject,
    ISortEvent
} from '../../../models';
import { LogbookTableSeries } from '../logbookTable.series';
import { seriesAdded, aggregationModes, wtDroppableDrop, groupIdentifierSeparator, toDictionary } from '../../../common';
import { ISeriesSelectorService, ITranslatedLoggerService } from '../../../services';

interface LogbookTableRow {
    id: string;
    name: string;
    series: LogbookTableSeries;
    viewModel: SeriesSelectorViewModel;
}

class LogbookTableSeriesController implements angular.IComponentController {
    aggregationModes: KeyValuePair<AggregationMode, string>[];
    sortConfig: ISortConfiguration;
    rows: LogbookTableRow[];
    seriesSelectorsCurrent: SeriesSelectorViewModel[];

    readonly formController: angular.IFormController;
    readonly isBindable: boolean;
    readonly series: LogbookTableSeries[];
    readonly seriesSelectors: SeriesSelectorViewModel[];

    static $inject = [
        '$scope',
        'seriesSelectorService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _seriesSelectorService: ISeriesSelectorService,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.sortConfig = {
            animation: 150,
            onSort: (evt: ISortEvent<LogbookTableRow>) => this.sort(evt),
            handle: '.drag-handle',
        };
        // Take aggregation modes except none
        const am = aggregationModes.map(d => d);
        am.splice(0, 1);
        this.aggregationModes = am;
        this._seriesSelectorService.getSeriesSelectorViewModels()
            .then(data => {
                this.seriesSelectorsCurrent = data;
                if (this.series) {
                    this.rows = this.buildRows(this.series, this.seriesSelectorsCurrent);
                }
            });

        this._$scope.$on(seriesAdded, (event, data: SeriesSelectorViewModel) => {
            this.addSerie(data);
        });

        this._$scope.$on(wtDroppableDrop, (event, data) => this.onDrop(data));
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['series']?.currentValue) {
            this.rows = this.buildRows(this.series, this.seriesSelectorsCurrent);
        }
        if (changesObj['seriesSelectors']?.currentValue) {
            this.seriesSelectorsCurrent = Object.assign({}, this.seriesSelectors);
        }
    }

    deleteSeries(series: LogbookTableSeries) {
        const index = this.series.indexOf(series);
        this.series.splice(index, 1);
        this.rows = this.buildRows(this.series, this.seriesSelectorsCurrent);
    }

    private onDrop(data: DropObject) {
        if (data.itemId) {
            this.updateSerie(data.binId, data.itemId);
        }
    }

    private updateSerie(oldId: string, newGuid: string) {
        const oldRow = this.rows.find(t => t.id === oldId);
        const selectors = toDictionary(this.seriesSelectorsCurrent);

        if (oldRow) {
            const serie = oldRow.series;
            const selector = selectors[newGuid];

            if (selector.objectType !== ObjectType.Identifier) {
                this._loggerService.logWarning('Only identifier are supported');
                return;
            }

            serie.guid = newGuid;
            serie.originalGuid = newGuid;
            serie.value = this._seriesSelectorService.getAvailableValueType(selector, this.series);
        }

        this.rows = this.buildRows(this.series, this.seriesSelectorsCurrent);
    }

    private addSerie(data: SeriesSelectorViewModel) {
        const row = {} as LogbookTableRow;
        if (data.objectType !== ObjectType.Identifier) {
            this._loggerService.logWarning('Only identifier are supported');
            return;
        }
        let sortOrder = 0;
        if (this.series?.length > 0) {
            const maxRow = _.maxBy(this.series, r => r.sortOrder);
            if (maxRow) {
                sortOrder = maxRow.sortOrder + 1;
            } else {
                sortOrder = this.series.length;
            }
        }
        const logbookSerie = {
            guid: data.guid,
            aggregationMode: AggregationMode.Average,
            isBoundToGroup: this.isBindable,
            originalGuid: data.guid,
            value: this._seriesSelectorService.getAvailableValueType(data, this.series),
            sortOrder: sortOrder
        };
        this.series.push(logbookSerie);
        this.rows = this.buildRows(this.series, this.seriesSelectorsCurrent);
    }

    private sort(evt: ISortEvent<LogbookTableRow>) {
        evt.models.forEach((s, i) => s.series.sortOrder = i);
        this.series.forEach(s => {
            const found = evt.models.find(m => m.series.guid === s.guid);
            if (found) {
                s.sortOrder = found.series.sortOrder;
            }
        });
        const series = evt.models.map(m => m.series);
        this.rows = this.buildRows(series, this.seriesSelectorsCurrent);
        this.formController.$setDirty();
    }

    private buildRows(series: LogbookTableSeries[], seriesSelectorsCurrent: SeriesSelectorViewModel[]): LogbookTableRow[] {
        const selectors = toDictionary(seriesSelectorsCurrent);
        return series ?
            series.map(s => this.reassignNames(selectors[s.guid], s))
            : [];
    }

    private reassignNames(sel: SeriesSelectorViewModel, serie: LogbookTableSeries): LogbookTableRow {
        const name = sel && sel.groupUniqueName
            ? sel.groupUniqueName + groupIdentifierSeparator + sel.name
            : sel && sel.name
                ? sel.name
                : '';

        return {
            name: name,
            viewModel: sel,
            series: serie,
            id: `${serie.value}+${serie.guid}`
        };
    }
}

export const wtLogbookTableSeries: ComponentDefinition = {
    componentId: 'wtLogbookTableSeries',
    controller: LogbookTableSeriesController,
    template: require('./logbookTableSeries.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '<?',
        isBindable: '<',
        series: '<',
        seriesSelectors: '<'
    }
};